import { useState, useRef, useEffect } from 'react';
import { Switch } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

export interface FilterBarProps {
  productsByFamily: Map<string, string[]>;
  selectedProductNames: string[];
  onFamilyToggle: (family: string, productNames: string[]) => void;
  onProductToggle: (productName: string) => void;
}

export const FilterBar = ({
  productsByFamily,
  selectedProductNames,
  onFamilyToggle,
  onProductToggle
}: FilterBarProps) => {
  const initialExpanded = useRef(new Set(productsByFamily.keys()));
  const [expandedFamilies, setExpandedFamilies] = useState<Set<string>>(initialExpanded.current);

  // Expand any newly added families automatically
  useEffect(() => {
    setExpandedFamilies((prev) => {
      const newFamilies = Array.from(productsByFamily.keys()).filter((f) => !prev.has(f));
      if (newFamilies.length === 0) return prev;
      return new Set([...prev, ...newFamilies]);
    });
  }, [productsByFamily]);

  const toggleExpand = (family: string) => {
    setExpandedFamilies((prev) => {
      const next = new Set(prev);
      next.has(family) ? next.delete(family) : next.add(family);
      return next;
    });
  };

  return (
    <div className="absolute top-[17px] left-[17px] z-10 w-[280px]">
      <div className="flex flex-col">
        <div className="bg-white px-3 py-2 border border-b-0 rounded-t border-grey-2 flex items-center gap-2">
          <div className="menu-filter-button" />
          <span className="font-sans text-slate-500 text-sm">Service Type</span>
        </div>
        <div className="bg-white border border-grey-2 rounded-b text-sm overflow-hidden">
          {Array.from(productsByFamily.entries()).map(([family, productNames]) => {
            const allSelected = productNames.every((n) => selectedProductNames.includes(n));
            const isExpanded = expandedFamilies.has(family);
            return (
              <div
                key={family}
                className="border-b last:border-b-0">
                <div className="flex items-center px-3 py-2 gap-2">
                  <Switch
                    checked={allSelected}
                    onChange={() => onFamilyToggle(family, productNames)}
                    size="small"
                    inputProps={{ 'aria-label': `Toggle ${family}` }}
                  />
                  <span className="flex-1 text-center font-semibold text-black">{family}</span>
                  <button
                    aria-label={isExpanded ? `Collapse ${family}` : `Expand ${family}`}
                    onClick={() => toggleExpand(family)}>
                    {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                  </button>
                </div>
                {isExpanded &&
                  productNames.map((name) => (
                    <div
                      key={name}
                      className="flex items-center px-3 py-2 pl-6 gap-2 border-t">
                      <Switch
                        checked={selectedProductNames.includes(name)}
                        onChange={() => onProductToggle(name)}
                        size="small"
                        inputProps={{ 'aria-label': `Toggle ${name}` }}
                      />
                      <span className="text-black">{name}</span>
                    </div>
                  ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
